import {
  Component,
  EventEmitter,
  Injector,
  Input,
  Output,
  ViewChild,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  filter,
  map,
  switchMap,
  debounceTime,
  from,
  of,
  catchError,
  finalize,
  tap,
} from 'rxjs';
import { ButtonComponent } from '@aprova-digital/design-system';

import { Types } from '../../../../../domain/types.domain';
import { City } from '../../../../../domain/city/entities/city.entity';
import { GetCepDetailsOutput } from '../../../../../domain/city/dtos/get-cep-details.output';
import {
  CheckboxComponent,
  InputComponent,
  LoadingSpinnerComponent,
  RadioComponent,
  RecaptchaComponent,
} from '../../../../../@ads/components';

import { SearchCepProviderInstruction } from '../../../../../builder/instructions/providers/search-cep.provider.instruction';

import { IaModeMobileLayout } from '../../../../utils/ia-mode-mobile';
import { AccountFormHandler, AddressFormValue } from '../../account-form.handler';

type CepAddressField = 'rua' | 'bairro' | 'cidade' | 'estado';
type CityField = 'cidade' | 'estado';

const CEP_ADDRESS_FIELDS: CepAddressField[] = ['rua', 'bairro', 'cidade', 'estado'];
const CITY_FIELDS: CityField[] = ['cidade', 'estado'];
const CEP_VALIDATORS = [Validators.required, Validators.maxLength(144)];

export type TenantCity = Pick<City, 'cidade' | 'estado'>;

@Component({
  selector: 'app-address-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RadioComponent,
    InputComponent,
    CheckboxComponent,
    ButtonComponent,
    RecaptchaComponent,
    LoadingSpinnerComponent,
  ],
  providers: [new SearchCepProviderInstruction()],
  templateUrl: './address-form.component.html',
  styleUrl: './address-form.component.scss',
})
export class AddressFormComponent {
  private readonly _searchCepProvider = inject(Types.SearchCepProvider);
  private readonly _accountFormHandler = inject(AccountFormHandler);
  private readonly _injector = inject(Injector);
  // APR-4028 — no celular do IA mode o endereço do CEP vira resumo e só número e complemento se digitam.
  private readonly _iaModeMobile = inject(IaModeMobileLayout);
  protected readonly iaMode = this._iaModeMobile.active;
  private readonly lastCepAddress = signal<GetCepDetailsOutput | null>(null);
  private searchedCep: string | null = null;
  private readonly editingCepAddress = signal(false);
  protected readonly withoutCep = signal(false);
  private readonly fixedCity = signal<Required<TenantCity> | null>(null);

  private readonly cepFilledFields = computed<CepAddressField[]>(() => {
    const address = this.lastCepAddress();
    return address && this._iaModeMobile.active() ? CEP_ADDRESS_FIELDS.filter((field) => !!address[field]) : [];
  });
  private readonly summarizedFields = computed<CepAddressField[]>(() =>
    this.editingCepAddress() ? [] : this.cepFilledFields()
  );
  private readonly lockedFields = computed<string[]>(() =>
    this.fixedCity() ? CITY_FIELDS : CITY_FIELDS.filter((field) => this.cepFilledFields().includes(field))
  );

  // Duas linhas, como no cartão do design: a rua e, embaixo, "Bairro · Cidade – UF".
  protected readonly cepSummary = computed(() => {
    const address = this.lastCepAddress();
    if (!address || !this.summarizedFields().length) {
      return null;
    }
    const cityUf = [address.cidade, address.estado].filter(Boolean).join(' – ');
    return {
      street: address.rua ?? '',
      place: [address.bairro, cityUf].filter(Boolean).join(' · '),
    };
  });
  protected readonly canEditCepAddress = computed(() =>
    this.summarizedFields().some((field) => field === 'rua' || field === 'bairro')
  );

  @Input()
  public isLoading: boolean = false;

  @Input()
  public hideRecaptcha: boolean = false;

  @Input()
  public blockedFields: string[] = [];

  @Input()
  public isLastStep: boolean = false;

  /** Cidade da prefeitura: no IA mode, fixa cidade e UF de quem não sabe o CEP. */
  @Input()
  public tenantCity: TenantCity | null | undefined = null;

  @Output()
  public setStepValidation = new EventEmitter<boolean>();

  @Output()
  public changeRecaptcha = new EventEmitter<string>();

  @ViewChild('inputCep', { static: false })
  public inputCep: InputComponent | undefined;

  @ViewChild('inputStreet', { static: false })
  public inputStreet: InputComponent | undefined;

  @ViewChild('inputNumber', { static: false })
  public inputNumber: InputComponent | undefined;

  @ViewChild('inputNeighborhood', { static: false })
  public inputNeighborhood: InputComponent | undefined;

  public loading = signal(false);
  public hasError = signal(false);
  public errorMessage = signal('');
  public cepNotice = signal<'none' | 'not_found' | 'unavailable'>('none');

  public noNumberAddress = signal(false);
  private isUpdatingFromCep = false;

  public form = new FormGroup({
    cep: new FormControl('', {
      nonNullable: true,
      validators: CEP_VALIDATORS,
    }),
    rua: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)],
    }),
    estado: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(144)],
    }),
    cidade: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(144)],
    }),
    bairro: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)],
    }),
    numero: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(144)],
    }),
    complemento: new FormControl('', {
      nonNullable: true,
      validators: [Validators.maxLength(144)],
    }),
    termos: new FormControl(false),
    recaptchaReactive: new FormControl<string | null>(null),
  });

  ngOnInit(): void {
    this.bindCepFields();

    if (this.isLastStep) {
      this.form.controls.termos.setValidators([Validators.requiredTrue]);
      this.form.controls.termos.setValue(false);

      if (!this.hideRecaptcha) {
        this.form.registerControl(
          'recaptchaReactive',
          new FormControl('', {
            nonNullable: true,
            validators: [Validators.required],
          })
        );
      }
    }

    this.form.valueChanges.subscribe((val) => {
      this.setStepValidation.emit(this.form.valid);
      const raw = this.form.getRawValue();
      this._accountFormHandler.setAddressInfo({
        cep: raw.cep,
        rua: raw.rua,
        estado: raw.estado,
        cidade: raw.cidade,
        bairro: raw.bairro,
        numero: raw.numero,
        complemento: raw.complemento ?? '',
        noNumberAddress: this.noNumberAddress(),
      });

      if (!this.hideRecaptcha && val.recaptchaReactive) {
        this.changeRecaptcha.emit(val.recaptchaReactive);
      }
    });

    const addressData = this._accountFormHandler.getAddressInfo();
    if (addressData) {
      this.isUpdatingFromCep = true;
      this.form.patchValue({
        cep: addressData.cep ?? '',
        rua: addressData.rua ?? '',
        estado: addressData.estado ?? '',
        cidade: addressData.cidade ?? '',
        bairro: addressData.bairro ?? '',
        numero: addressData.numero ?? '',
        complemento: addressData.complemento ?? '',
      });
      this.isUpdatingFromCep = false;
      const shouldRestoreNoNumber = addressData.noNumberAddress === true;
      if (shouldRestoreNoNumber) {
        this.noNumberAddress.set(true);
        this.applyNoNumberState();
      }
    }

    this.restoreIaModeAddress(addressData);
    this.form.updateValueAndValidity();
  }

  isFieldVisible(name: 'cep' | CepAddressField): boolean {
    return name === 'cep' ? !this.withoutCep() : !this.summarizedFields().includes(name);
  }

  editCepAddress(): void {
    this.editingCepAddress.set(true);
    this.focusAfterRender(() => this.inputStreet);
  }

  useAddressWithoutCep(): void {
    this.enterAddressWithoutCep();
    this.focusAfterRender(() => this.inputStreet);
  }

  useCep(): void {
    this.withoutCep.set(false);
    this._accountFormHandler.setAddressWithoutCep(false);
    this.form.controls.cep.setValidators(CEP_VALIDATORS);
    this.form.controls.cep.updateValueAndValidity({ emitEvent: false });
    if (this.fixedCity()) {
      this.fixedCity.set(null);
      this.form.patchValue({ cidade: '', estado: '' }, { emitEvent: false });
    }
    this.syncCityControls();
    this.focusAfterRender(() => this.inputCep);
  }

  private enterAddressWithoutCep(): void {
    this.withoutCep.set(true);
    this._accountFormHandler.setAddressWithoutCep(true);
    this.cepNotice.set('none');
    this.searchedCep = null;
    this.forgetCepAddress();

    this.form.controls.cep.setValue('', { emitEvent: false });
    this.form.controls.cep.setValidators([Validators.maxLength(144)]);
    this.form.controls.cep.updateValueAndValidity({ emitEvent: false });

    const city = this.reliableTenantCity();
    this.fixedCity.set(city);
    if (city) {
      this.form.patchValue(city, { emitEvent: false });
    }
    this.syncCityControls();
  }

  /** Cidade e UF só ficam fixas com nome e sigla de 2 letras; senão a pessoa digita. */
  private reliableTenantCity(): Required<TenantCity> | null {
    const cidade = this.tenantCity?.cidade?.trim();
    const estado = this.tenantCity?.estado?.trim();
    return cidade && estado && /^[a-z]{2}$/i.test(estado) ? { cidade, estado: estado.toUpperCase() } : null;
  }

  private restoreIaModeAddress(address: AddressFormValue | null): void {
    if (!this._iaModeMobile.active()) {
      return;
    }

    const cep = address?.cep?.replace('-', '') ?? '';
    // A retentativa depois de erro no cadastro volta do localStorage sem o modo: endereço sem CEP é esse modo.
    if (this._accountFormHandler.isAddressWithoutCep() || (!cep && !!address?.rua)) {
      this.enterAddressWithoutCep();
      return;
    }

    if (cep.length === 8) {
      this.searchedCep = cep;
      this.lastCepAddress.set({ rua: address?.rua, bairro: address?.bairro, cidade: address?.cidade, estado: address?.estado });
    }
  }

  private onCepTyped(cep: string): void {
    if (!this._iaModeMobile.active() || cep === this.searchedCep) {
      return;
    }
    this.searchedCep = null;
    this.forgetCepAddress();
  }

  /** Limpa o que veio do CEP anterior para não misturar endereços. */
  private forgetCepAddress(): void {
    const address = this.lastCepAddress();
    this.lastCepAddress.set(null);
    this.editingCepAddress.set(false);
    if (!address) {
      return;
    }

    const filled = CEP_ADDRESS_FIELDS.filter((field) => !!address[field]);
    this.form.patchValue(Object.fromEntries(filled.map((field) => [field, ''])), { emitEvent: false });
    this.syncCityControls();
  }

  /** O `aprova-input` só troca o disabled na próxima detecção e sem emitir; aqui a validade do passo fica certa na hora. */
  private syncCityControls(): void {
    for (const field of CITY_FIELDS) {
      const control = this.form.controls[field];
      if (this.isFieldBlocked(field)) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    }
    this.form.updateValueAndValidity();
  }

  private focusAfterRender(input: () => InputComponent | undefined): void {
    afterNextRender(() => input()?.focusInput(), { injector: this._injector });
  }

  private applyNoNumberState(): void {
    const numeroControl = this.form.get('numero');
    numeroControl?.setValue('');
    numeroControl?.setValidators([]);
    numeroControl?.disable();
    numeroControl?.updateValueAndValidity();
  }

  isFieldBlocked(name: string): boolean {
    return this.blockedFields.includes(name) || this.lockedFields().includes(name);
  }

  toogleNoNumberAddress(): void {
    this.noNumberAddress.update((val) => !val);
    const numeroControl = this.form.get('numero');

    if (this.noNumberAddress()) {
      numeroControl?.setValue('');
      numeroControl?.setValidators([]);
      numeroControl?.disable();
    } else {
      numeroControl?.enable();
      numeroControl?.setValue('');
      numeroControl?.setValidators([Validators.required, Validators.maxLength(144)]);
    }

    numeroControl?.updateValueAndValidity();
    this.form.updateValueAndValidity();
  }

  private bindCepFields(): void {
    this.form.controls.cep.valueChanges
      .pipe(
        filter(() => !this.isUpdatingFromCep),
        tap(() => this.cepNotice.set('none')),
        map((cep) => cep.replace('-', '')),
        tap((cep) => this.onCepTyped(cep)),
        filter((cep) => cep.length === 8),
        debounceTime(500),
        filter((cep) => cep !== this.searchedCep),
        switchMap((cep) => {
          this.searchedCep = cep;
          this.loading.set(true);
          return from(this._searchCepProvider.handle(cep)).pipe(
            catchError(() =>
              of<GetCepDetailsOutput>({ error: 'unavailable' })
            ),
            finalize(() => this.loading.set(false))
          );
        })
      )
      .subscribe((address: GetCepDetailsOutput) => {
        if (this.withoutCep()) {
          return;
        }

        if (address.error) {
          this.cepNotice.set(address.error);
          if (this._iaModeMobile.active()) {
            this.forgetCepAddress();
          }
          this.lastCepAddress.set(null);
          return;
        }

        this.cepNotice.set('none');
        this.lastCepAddress.set(address);

        this.isUpdatingFromCep = true;
        const patch: Record<string, string> = {};
        if (address.rua) patch['rua'] = address.rua;
        if (address.cidade) patch['cidade'] = address.cidade;
        if (address.estado) patch['estado'] = address.estado;
        if (address.bairro) patch['bairro'] = address.bairro;
        this.form.patchValue(patch);
        this.isUpdatingFromCep = false;

        const inputComponent = this.getNextAddressInputToFill(address);
        inputComponent?.inputRef?.nativeElement.focus();
      });
  }

  private getNextAddressInputToFill(
    address: GetCepDetailsOutput
  ): InputComponent | undefined {
    if (!address.rua) {
      return this.inputStreet;
    }

    if (!address.bairro) {
      return this.inputNeighborhood;
    }

    return this.inputNumber;
  }
}
